import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/units")({
  component: UnitsPage,
});

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchUnits,
  useUnitMutations,
  type UnitOrdering,
} from "@/hooks/api/inventory/useCatalog";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDebounce } from "@/hooks/use-debounce";
import { UnitTable } from "@/components/inventory/tables/UnitTable";
import { UnitFormSheet } from "@/components/inventory/forms/UnitFormSheet";
import type { UnitOfMeasure, UnitOfMeasurePayload } from "@/types/inventory";

function UnitsPage() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // FILTERS + ORDERING
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        abbreviation: SortDirection;
        is_active: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      UnitOrdering
    >({
      name: null,
      abbreviation: null,
      is_active: null,
      created_at: null,
      updated_at: null,
    });

  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(searchInput, 500);
  const [statusFilter, setStatusFilter] = useState<
    "true" | "false" | undefined
  >();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editing, setEditing] = useState<UnitOfMeasure | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UnitOfMeasure | null>(null);

  // QUERIES & MUTATIONS
  const unitsQuery = useFetchUnits({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    is_active:
      statusFilter === "true"
        ? true
        : statusFilter === "false"
          ? false
          : undefined,
  });
  const units = unitsQuery.data?.results ?? [];
  const count = unitsQuery.data?.count ?? 0;

  const { createUnit, updateUnit, deleteUnit } = useUnitMutations();

  const isMutating =
    createUnit.isPending || updateUnit.isPending || deleteUnit.isPending;
  const isLoading = unitsQuery.isPending || isMutating;
  const isFetching = unitsQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, statusFilter, setPage]);

  const resetFilters = () => {
    setOrdering({
      name: null,
      abbreviation: null,
      is_active: null,
      created_at: null,
      updated_at: null,
    });
    setSearchInput("");
    resetDebounce();
    setStatusFilter(undefined);
    setPage(1);
  };

  const handleSubmit = async (data: UnitOfMeasurePayload) => {
    if (editing) {
      await updateUnit.mutateAsync({ id: editing.id, payload: data });
      toast.success("Unit updated");
    } else {
      await createUnit.mutateAsync(data);
      toast.success("Unit created");
    }
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUnit.mutateAsync({ id: deleteTarget.id });
      toast.success("Unit deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Units of Measure"
          description="Manage measurement units for inventory items"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={() => {
            setEditing(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Add Unit"
          isLoading={isFetching}
        />
        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            placeholder="Search units..."
            value={searchInput}
            onChange={setSearchInput}
          />
          <SelectFilter
            placeholder="Status"
            options={[
              { label: "Active", value: "true" },
              { label: "Inactive", value: "false" },
            ]}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
          />
        </FiltersPanel>
        <UnitTable
          units={units}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          onView={(u) => {
            setEditing(u);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(u) => {
            setEditing(u);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(u) => setDeleteTarget(u)}
        />
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
        <UnitFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleSubmit}
          editing={editing}
          isLoading={isMutating}
          isViewOnly={isViewOnly}
        />
        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Unit"
          description={`Delete unit "${deleteTarget?.name}"?`}
          itemName={deleteTarget?.name}
          isLoading={isMutating}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
